import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { LayoutGrid, MessageSquare, Key, Globe, LogOut, LogIn, XCircle, ArrowRight as ArrowRightIcon, Menu, Coins, Home, Box, Zap } from 'lucide-react'
import { useAuth } from '../contexts/SupabaseAuthContext'
import { getUserTokenBalance } from '../services/unifiedTokenService'

// --- HELPER COMPONENTS ---

interface FloatingNavItemProps {
  icon: any
  label: string
  color: string
  onClick: () => void
  forceExpanded?: boolean
}

function FloatingNavItem({ icon: Icon, label, color, onClick, forceExpanded = false }: FloatingNavItemProps) {
  const colorClasses: any = {
    cyan: 'border-cyan-500 text-cyan-400 bg-[#0f0f0f] hover:bg-cyan-950 shadow-[0_0_15px_rgba(6,182,212,0.2)] group-hover:border-cyan-400 group-hover:shadow-[0_0_25px_rgba(6,182,212,0.5)]',
    purple: 'border-purple-500 text-purple-400 bg-[#0f0f0f] hover:bg-purple-950 shadow-[0_0_15px_rgba(168,85,247,0.2)] group-hover:border-purple-400 group-hover:shadow-[0_0_25px_rgba(168,85,247,0.5)]',
    yellow: 'border-yellow-500 text-yellow-400 bg-[#0f0f0f] hover:bg-yellow-950 shadow-[0_0_15px_rgba(234,179,8,0.2)] group-hover:border-yellow-400 group-hover:shadow-[0_0_25px_rgba(234,179,8,0.5)]',
    blue: 'border-blue-500 text-blue-400 bg-[#0f0f0f] hover:bg-blue-950 shadow-[0_0_15px_rgba(59,130,246,0.2)] group-hover:border-blue-400 group-hover:shadow-[0_0_25px_rgba(59,130,246,0.5)]',
    red: 'border-red-500 text-red-400 bg-[#0f0f0f] hover:bg-red-950 shadow-[0_0_15px_rgba(239,68,68,0.2)] group-hover:border-red-400 group-hover:shadow-[0_0_25px_rgba(239,68,68,0.5)]',
    green: 'border-green-500 text-green-400 bg-[#0f0f0f] hover:bg-green-950 shadow-[0_0_15px_rgba(34,197,94,0.2)] group-hover:border-green-400 group-hover:shadow-[0_0_25px_rgba(34,197,94,0.5)]',
  }
  
  const textColors: any = {
    cyan: 'text-cyan-400',
    purple: 'text-purple-400',
    yellow: 'text-yellow-400',
    blue: 'text-blue-400',
    red: 'text-red-400',
    green: 'text-green-400',
  }

  const activeClass = colorClasses[color] || colorClasses.cyan
  const textColor = textColors[color] || 'text-white'

  return (
    <motion.button
      onClick={onClick}
      initial="initial"
      whileHover="hover"
      animate={forceExpanded ? "hover" : "initial"}
      className="pointer-events-auto relative flex items-center justify-end group py-1"
    >
      <motion.div
        variants={{
          initial: { width: 0, opacity: 0, x: 20 },
          hover: { width: 'auto', opacity: 1, x: -8 } // Slide out to left
        }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        className={`overflow-hidden bg-[#0a0a0a] border border-white/20 rounded-l-xl flex items-center h-10 absolute right-10 z-0 shadow-xl`}
      >
        <span className={`whitespace-nowrap px-4 text-sm font-bold tracking-wide uppercase ${textColor}`}>
          {label}
        </span>
      </motion.div>
      
      <div className={`
        relative z-10 w-12 h-12 rounded-xl border-2 
        flex items-center justify-center transition-all duration-300 shadow-lg
        group-hover:scale-110 ${activeClass}
      `}>
        <Icon size={24} strokeWidth={2} />
      </div>
    </motion.button>
  )
}

export default function FloatingSidebar() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [isNavHovered, setIsNavHovered] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [tokenBalance, setTokenBalance] = useState<number>(0)
  const [isMobile, setIsMobile] = useState(false)
  const LAUNCHER_WEB_URL = 'https://app.overpixel.online'

  const openLauncherWeb = () => {
    window.open(LAUNCHER_WEB_URL, '_blank', 'noopener,noreferrer')
    setIsNavHovered(false)
    setIsMobileMenuOpen(false)
  }

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    const updateBalance = () => {
      if (user) {
        getUserTokenBalance(user.id)
          .then(setTokenBalance)
          .catch(console.error)
      } else {
        setTokenBalance(0)
      }
    }

    // Busca inicial
    updateBalance()

    // Listener para atualizações de saldo (débitos, créditos, etc)
    window.addEventListener('tokenBalanceUpdated', updateBalance)
    
    return () => {
      window.removeEventListener('tokenBalanceUpdated', updateBalance)
    }
  }, [user])

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  return (
    <>
      <AnimatePresence>
          {/* User Status Widget (Top Right) */}
          <div key="user-status-widget" className="fixed right-6 top-6 z-[9999999] flex flex-col items-end gap-2 pointer-events-none">
              {user ? (
                <motion.div 
                  className="pointer-events-auto flex items-center gap-3 bg-[#0a0a0a]/90 backdrop-blur-xl border border-cyan-500/30 p-1.5 pl-5 rounded-full shadow-[0_0_20px_rgba(6,182,212,0.15)] hover:bg-cyan-950/30 hover:border-cyan-400/50 hover:shadow-[0_0_30px_rgba(6,182,212,0.3)] transition-all duration-300 group cursor-default"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex flex-col items-end mr-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5 mb-0.5">
                        <Coins size={12} className="text-yellow-400" />
                        <span className="text-[11px] font-bold text-yellow-400 uppercase tracking-widest leading-none">{tokenBalance} TOKENS</span>
                    </div>
                    <span className="text-xs text-cyan-100/80 font-mono tracking-tight">{user.email?.split('@')[0]}</span>
                  </div>
                  <div className="relative shrink-0">
                    {user.user_metadata?.avatar_url || user.user_metadata?.picture ? (
                        <img 
                            src={user.user_metadata.avatar_url || user.user_metadata.picture} 
                            alt="User" 
                            className="w-10 h-10 rounded-full border-2 border-cyan-500/50 shadow-lg object-cover"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-[#0f172a] border-2 border-cyan-500 flex items-center justify-center text-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.3)] text-sm font-bold">
                            {user.email?.[0].toUpperCase()}
                        </div>
                    )}
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#0a0a0a] rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                  </div>
                </motion.div>
              ) : (
                <motion.button 
                  onClick={() => navigate('/auth/login')}
                  className="pointer-events-auto flex items-center gap-3 bg-white/10 backdrop-blur-xl border border-white/20 p-1.5 pl-4 rounded-full shadow-2xl hover:bg-white/20 transition-colors group"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                    <span className="text-xs font-bold text-white uppercase tracking-wider hidden group-hover:block transition-all">Entrar</span>
                    <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white">
                      <LogIn size={16} />
                    </div>
                </motion.button>
              )}
              
              {/* Mobile Menu Toggle removed (was duplicate) */}
          </div>

          {/* Navigation Icons (Right Side - Floating) */}
          <div 
              key="nav-icons"
              className="fixed right-6 top-32 z-[9999999] flex flex-col items-end pointer-events-none"
          >
              {user && (
                <motion.div 
                  className="flex flex-col items-end pointer-events-auto"
                  onMouseEnter={() => !isMobile && setIsNavHovered(true)}
                  onMouseLeave={() => !isMobile && setIsNavHovered(false)}
                >
                  {/* Always Visible First Icon (Trigger) */}
                  <FloatingNavItem 
                    icon={isNavHovered ? XCircle : Menu} 
                    label={isNavHovered ? "Fechar" : "Menu"} 
                    color={isNavHovered ? "red" : "green"} 
                    onClick={() => setIsNavHovered(!isNavHovered)} 
                    forceExpanded={isMobile}
                  />

                  {/* Expandable Menu Items */}
                  <AnimatePresence>
                    {isNavHovered && (
                       <motion.div
                         initial="hidden"
                         animate="visible"
                         exit="hidden"
                         variants={{
                           visible: { transition: { staggerChildren: 0.05 } },
                           hidden: { transition: { staggerChildren: 0.05, staggerDirection: -1 } }
                         }}
                         className="flex flex-col items-end gap-3 mt-3"
                       >
                          <motion.div variants={{ hidden: { opacity: 0, y: -10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}>
                            <FloatingNavItem 
                              icon={Home} 
                              label="Home" 
                              color="green" 
                              onClick={() => navigate('/')} 
                              forceExpanded={isMobile}
                            />
                          </motion.div>
                          <motion.div variants={{ hidden: { opacity: 0, y: -10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}>
                            <FloatingNavItem 
                              icon={LayoutGrid} 
                              label="Loja" 
                              color="cyan" 
                              onClick={() => navigate('/softwares')} 
                              forceExpanded={isMobile}
                            />
                          </motion.div>
                          <motion.div variants={{ hidden: { opacity: 0, y: -10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}>
                            <FloatingNavItem 
                              icon={Box} 
                              label="Aplicativos" 
                              color="purple" 
                              onClick={openLauncherWeb} 
                              forceExpanded={isMobile}
                            />
                          </motion.div>
{/*                          <motion.div variants={{ hidden: { opacity: 0, y: -10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}>
                            <FloatingNavItem 
                              icon={MessageSquare} 
                              label="AI Chat" 
                              color="purple" 
                              onClick={() => navigate('/chat')} 
                              forceExpanded={isMobile}
                            />
                          </motion.div>*/}
                          <motion.div variants={{ hidden: { opacity: 0, y: -10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}>
                            <FloatingNavItem 
                              icon={Key} 
                              label="Minhas Licenças" 
                              color="yellow" 
                              onClick={() => navigate('/my-licenses')} 
                              forceExpanded={isMobile}
                            />
                          </motion.div>
                          <motion.div variants={{ hidden: { opacity: 0, y: -10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}>
                            <FloatingNavItem 
                              icon={Zap} 
                              label="Tokens" 
                              color="yellow" 
                              onClick={() => navigate('/tokens')} 
                              forceExpanded={isMobile}
                            />
                          </motion.div>
{/*                          <motion.div variants={{ hidden: { opacity: 0, y: -10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}>
                            <FloatingNavItem 
                              icon={Globe} 
                              label="Overpixel World" 
                              color="blue" 
                              onClick={() => navigate('/overpixel-world')} 
                              forceExpanded={isMobile}
                            />
                          </motion.div>*/}
                          
                          <motion.div variants={{ hidden: { opacity: 0, y: -10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}>
                            <div className="h-px w-8 bg-white/10 my-2 mr-2" />
                          </motion.div>
                          
                          <motion.div variants={{ hidden: { opacity: 0, y: -10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}>
                            <FloatingNavItem 
                              icon={LogOut} 
                              label="Sair" 
                              color="red" 
                              onClick={handleLogout} 
                              forceExpanded={isMobile}
                            />
                          </motion.div>
                       </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
          </div>
      </AnimatePresence>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
            <motion.div 
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 20, stiffness: 100 }}
                className="fixed inset-0 z-[200] bg-[#050505]/95 backdrop-blur-xl p-8 flex flex-col pointer-events-auto md:hidden overflow-y-auto"
            >
                <div className="flex justify-between items-center mb-12">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white]" />
                        <span className="text-xl font-black tracking-tight text-white">OVERPIXEL</span>
                    </div>
                    <button 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="p-2 text-white/50 hover:text-white transition-colors"
                    >
                        <XCircle size={32} />
                    </button>
                </div>

                <div className="flex-1 flex flex-col gap-6">
                    {user ? (
                        <>
                            <div className="relative bg-black border border-white/10 p-6 rounded-2xl mb-4 z-50 isolate" style={{ backgroundColor: '#000000' }}>
                                {/* Solid Background Layer */}
                                <div className="absolute inset-0 bg-black rounded-2xl -z-10" style={{ backgroundColor: '#000000' }} />
                                
                                <div className="relative z-10">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                        <h2 className="text-xl font-semibold text-white">Conectado</h2>
                                    </div>
                                    <p className="text-gray-400 text-sm truncate">{user.email}</p>
                                </div>
                            </div>

                            <button
                                onClick={() => {
                                  navigate('/')
                                  setIsMobileMenuOpen(false)
                                }}
                                className="w-full bg-green-950 border border-green-500/20 text-green-400 font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-between active:scale-95"
                            >
                                <span className="text-lg">Home</span>
                                <Home className="w-5 h-5" />
                            </button>

                            <button
                                onClick={() => {
                                  navigate('/softwares')
                                  setIsMobileMenuOpen(false)
                                }}
                                className="w-full bg-cyan-950 border border-cyan-500/20 text-cyan-400 font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-between active:scale-95"
                            >
                                <span className="text-lg">Loja</span>
                                <ArrowRightIcon className="w-5 h-5" />
                            </button>

                            <button
                                onClick={() => {
                                  openLauncherWeb()
                                }}
                                className="w-full bg-purple-950/50 border border-purple-500/20 text-purple-400 font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-between active:scale-95"
                            >
                                <span className="text-lg">Overbuilder Sandbox</span>
                                <ArrowRightIcon className="w-5 h-5" />
                            </button>

{/*                            <button
                                onClick={() => {
                                  navigate('/chat')
                                  setIsMobileMenuOpen(false)
                                }}
                                className="w-full bg-purple-950 border border-purple-500/20 text-purple-400 font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-between active:scale-95"
                                >
                                <span className="text-lg">AI Chat</span>
                                <ArrowRightIcon className="w-5 h-5" />
                            </button>*/}

                            <button
                                  onClick={() => {
                                    navigate('/my-licenses')
                                    setIsMobileMenuOpen(false)
                                  }}
                                  className="w-full bg-yellow-950/50 border border-yellow-500/20 text-yellow-400 font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-between active:scale-95"
                              >
                                  <div className="flex items-center gap-3">
                                      <span className="text-lg">Minhas Licenças</span>
                                  </div>
                                  <ArrowRightIcon className="w-5 h-5" />
                              </button>

                              <button
                                  onClick={() => {
                                    navigate('/tokens')
                                    setIsMobileMenuOpen(false)
                                  }}
                                  className="w-full bg-yellow-950/50 border border-yellow-500/20 text-yellow-400 font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-between active:scale-95"
                              >
                                  <div className="flex items-center gap-3">
                                      <span className="text-lg">Tokens</span>
                                  </div>
                                  <Zap className="w-5 h-5" />
                              </button>

{/*                              <button
                                  onClick={() => {
                                    navigate('/overpixel-world')
                                    setIsMobileMenuOpen(false)
                                  }}
                                  className="w-full bg-blue-950/50 border border-blue-500/20 text-blue-400 font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-between active:scale-95"
                              >
                                  <span className="text-lg">Overpixel World</span>
                                  <ArrowRightIcon className="w-5 h-5" />
                              </button>*/}

                              <div className="h-px bg-white/10 my-4" />

                              <button
                                  onClick={() => {
                                    handleLogout()
                                    setIsMobileMenuOpen(false)
                                  }}
                                  className="w-full bg-red-950/30 border border-red-500/20 text-red-400 font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-between active:scale-95"
                              >
                                  <span className="text-lg">Sair</span>
                                  <LogOut className="w-5 h-5" />
                              </button>
                        </>
                    ) : (
                        <button
              onClick={() => {
                navigate('/auth/login')
                setIsMobileMenuOpen(false)
              }}
              className="w-full bg-white text-black font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-between active:scale-95"
            >
                            <span className="text-lg">Fazer Login</span>
                            <LogIn className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function UserIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  )
}
